import React from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import Select from '../../../components/widgets/Select';
import { Toggle, WarningMessage } from '../../../components/widgets';
import QuestionMark from '../../../components/widgets/QuestionMark';
import Label from '../../../components/foundations/Label';
import FormInnerSection from '../FormInnerSection';
import Input from '../Input';
import {
    ATTRIBUTION_TAB_IDS,
    PLATFORM_TEMPLATES,
    POSTBACK_EVENT_TYPE_OPTIONS,
    POSTBACK_METHOD_OPTIONS,
    POSTBACK_METHODS,
    TRANSLATE_PREFIX,
    urlField,
} from '../../consts';
import useConfirmDialog from '../../useConfirmDialog';
import { useFieldValidityContext } from '../../useFieldValidity';
import { useTabUpdater } from '../../formUpdaters';
import {
    PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS,
    PLATFORM_TEMPLATE_REQUIRED_QUERY_PARAM_KEYS,
    PLATFORM_TEMPLATE_SECURITY_TOKEN_KEYS,
    POSTBACK_URL_DOMAIN_REGEX,
    POSTBACK_URL_SECURITY_TOKEN_REGEX,
    buildPostbackUrlForTemplate,
    extractPostbackUrlDomain,
    extractRequiredQueryParamValue,
    extractSecurityToken,
    setRequiredQueryParamInUrl,
    setSecurityTokenInUrl,
} from '../../platformTemplateDefaults';
import css from './PostbackUrlBaseSection.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;

const POSTBACK_URL_HINT_TRANSLATION_KEYS = {
    [PLATFORM_TEMPLATES.HASOFFERS]: `${TAB_PREFIX}.TUNE_DOMAIN_HINT`,
    [PLATFORM_TEMPLATES.AFFISE]: `${TAB_PREFIX}.AFFISE_DOMAIN_HINT`,
    [PLATFORM_TEMPLATES.EVERFLOW]: `${TAB_PREFIX}.EVERFLOW_DOMAIN_HINT`,
};

const PostbackUrlBaseSection = ({ translate, readOnly = false }) => {
    const { values } = useFormikContext();
    const updateTab = useTabUpdater(TAB_ID);
    const tabData = values.formData?.[TAB_ID] || {};
    const { confirm, dialog } = useConfirmDialog();

    const eventsAndPlatforms = tabData.postback_events_and_platforms || [];
    const selectedEventTypes = eventsAndPlatforms.filter(e => (e.platforms || []).length > 0).map(e => e.event_type);
    const hasAnySelection = selectedEventTypes.length > 0;
    const platformTemplate = values.formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const postbackUrlHintKey = POSTBACK_URL_HINT_TRANSLATION_KEYS[platformTemplate];
    // Affiliate templates lock the URL shape — only the domain piece is editable.
    const hasUrlPattern = !!PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS[platformTemplate];
    const domainValue = hasUrlPattern ? extractPostbackUrlDomain(platformTemplate, tabData.postback_url_base) : '';

    const method = tabData.postback_method ?? POSTBACK_METHODS.GET;
    const differentDomains = !!tabData.different_domains_per_postback;

    const setField = (field, value) => updateTab({ [field]: value });

    const methodOptions = POSTBACK_METHOD_OPTIONS.map(m => ({ value: m, label: m }));
    const selectedMethod = methodOptions.find(o => o.value === method) || methodOptions[0];

    const hasAnyPerEventUrl = () => POSTBACK_EVENT_TYPE_OPTIONS.some(et => !!tabData[urlField(et)]?.trim());

    const clearPerEventUrls = extraTabFields =>
        updateTab(prevTab => {
            const nextTab = { ...prevTab, ...extraTabFields };
            POSTBACK_EVENT_TYPE_OPTIONS.forEach(et => {
                nextTab[urlField(et)] = undefined;
            });
            return nextTab;
        });

    const handleDifferentDomainsToggle = e => {
        const { checked } = e.target;
        if (!checked && hasAnyPerEventUrl()) {
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DISABLE_DIFFERENT_DOMAINS_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DISABLE_DIFFERENT_DOMAINS_TEXT`,
                onConfirm: () => clearPerEventUrls({ different_domains_per_postback: false }),
            });
            return;
        }
        setField('different_domains_per_postback', checked);
    };

    const apiValid = useFieldValidityContext();

    const shouldShowUrlError = (field, trimmedValue) => !readOnly && !!trimmedValue && apiValid[field] === false;

    const baseUrlInvalid = shouldShowUrlError('postback_url_base', tabData.postback_url_base?.trim());
    const eventUrlInvalid = eventType => {
        const key = urlField(eventType);
        return shouldShowUrlError(key, tabData[key]?.trim());
    };
    const urlHint = translate(`${TRANSLATE_PREFIX}.ERRORS.POSTBACK_URL_INVALID_HINT`);
    const domainHint = translate(`${TRANSLATE_PREFIX}.ERRORS.POSTBACK_URL_DOMAIN_INVALID_HINT`);
    const domainInvalidChars = !!domainValue && !POSTBACK_URL_DOMAIN_REGEX.test(domainValue);

    // UI-only security token input. The token is appended to `postback_url_base` as
    // `?<key>=<value>` and re-extracted on next render, so no separate form field is needed.
    const securityTokenKey = PLATFORM_TEMPLATE_SECURITY_TOKEN_KEYS[platformTemplate];
    const hasSecurityToken = !!securityTokenKey;
    const securityTokenValue = hasSecurityToken
        ? extractSecurityToken(platformTemplate, tabData.postback_url_base)
        : '';
    const securityTokenInvalid =
        hasSecurityToken && !!securityTokenValue && !POSTBACK_URL_SECURITY_TOKEN_REGEX.test(securityTokenValue);
    const securityTokenHint = translate(`${TRANSLATE_PREFIX}.ERRORS.POSTBACK_URL_SECURITY_TOKEN_INVALID_HINT`);

    // Required template query param (currently only Everflow's `nid`). Appended to the URL
    // identically to the security token; submit validation requires a non-empty value.
    const requiredQueryParamKey = PLATFORM_TEMPLATE_REQUIRED_QUERY_PARAM_KEYS[platformTemplate];
    const hasRequiredQueryParam = !!requiredQueryParamKey;
    const requiredQueryParamValue = hasRequiredQueryParam
        ? extractRequiredQueryParamValue(platformTemplate, tabData.postback_url_base)
        : '';
    const requiredQueryParamInvalid =
        hasRequiredQueryParam &&
        !!requiredQueryParamValue &&
        !POSTBACK_URL_SECURITY_TOKEN_REGEX.test(requiredQueryParamValue);
    const requiredQueryParamHint = translate(`${TRANSLATE_PREFIX}.ERRORS.POSTBACK_URL_NID_INVALID_HINT`);
    // For affiliate templates the input is disabled — show the assembled pattern with the
    // placeholder domain so the user can see the shape that will be saved. If the user has
    // already typed a security token, weave it into the placeholder too so the preview matches
    // what will be saved once the domain is filled in.
    const domainPlaceholder = translate(`${TAB_PREFIX}.POSTBACK_URL_DOMAIN_PLACEHOLDER`);
    const baseUrlPlaceholder = hasUrlPattern
        ? setSecurityTokenInUrl(
              platformTemplate,
              setRequiredQueryParamInUrl(
                  platformTemplate,
                  buildPostbackUrlForTemplate(platformTemplate, domainPlaceholder),
                  requiredQueryParamValue
              ),
              securityTokenValue
          )
        : translate(`${TAB_PREFIX}.POSTBACK_URL_BASE_PLACEHOLDER`);
    // For affiliate templates: if the domain is empty (never typed, or typed-then-cleared so the
    // stored value is `pattern + ""`), display the pattern with the placeholder domain instead of
    // the stored mid-edit string. Underlying form value stays as-is — this only changes display.
    const displayedBaseUrl = hasUrlPattern && !domainValue ? baseUrlPlaceholder : tabData.postback_url_base || '';

    return (
        <FormInnerSection
            titleKey={`${TAB_PREFIX}.POSTBACK_URL_BASE_LABEL`}
            descriptionKey={`${TAB_PREFIX}.POSTBACK_URL_BASE_SUBTITLE`}
            anchor="postback_url_base"
        >
            {postbackUrlHintKey && (
                <WarningMessage type="info" showIcon={false} message={<Translate id={postbackUrlHintKey} />} />
            )}
            {hasUrlPattern && (
                <div className={css.domainField}>
                    <Label text={`${TAB_PREFIX}.POSTBACK_URL_DOMAIN_LABEL`} type="p" className={css.domainLabel} />
                    <Input
                        value={domainValue}
                        placeholder={readOnly ? undefined : translate(`${TAB_PREFIX}.POSTBACK_URL_DOMAIN_PLACEHOLDER`)}
                        onChange={val => {
                            // All pieces empty → clear `postback_url_base` so the BE doesn't see
                            // `pattern + ""`. Otherwise assemble pattern + domain and weave in
                            // any already-typed security token / required query param.
                            if (!val && !securityTokenValue && !requiredQueryParamValue) {
                                setField('postback_url_base', undefined);
                                return;
                            }
                            let next = buildPostbackUrlForTemplate(platformTemplate, val || '');
                            if (requiredQueryParamValue) {
                                next = setRequiredQueryParamInUrl(platformTemplate, next, requiredQueryParamValue);
                            }
                            if (securityTokenValue) {
                                next = setSecurityTokenInUrl(platformTemplate, next, securityTokenValue);
                            }
                            setField('postback_url_base', next);
                        }}
                        disabled={readOnly}
                        containerClass={css.domainInput}
                        required={!readOnly}
                        error={domainInvalidChars ? domainHint : undefined}
                    />
                </div>
            )}
            <div className={css.methodUrlRow}>
                <Select
                    options={methodOptions}
                    value={selectedMethod}
                    onChange={opt => setField('postback_method', opt ? opt.value : POSTBACK_METHODS.GET)}
                    isSearchable={false}
                    isClearable={false}
                    isDisabled={readOnly}
                    className={css.methodSelect}
                />
                <Input
                    // Affiliate-template URLs can run long once domain + token are filled in;
                    // render as a wrapping textarea so the user sees the whole assembled URL.
                    type={hasUrlPattern ? 'textarea' : 'text'}
                    inputConfig={hasUrlPattern ? { rows: 1 } : undefined}
                    value={displayedBaseUrl}
                    placeholder={readOnly ? undefined : baseUrlPlaceholder}
                    onChange={val => setField('postback_url_base', val)}
                    disabled={readOnly || hasUrlPattern}
                    containerClass={css.urlBaseInput}
                    inputClassName={hasUrlPattern ? css.urlBaseTextArea : undefined}
                    error={!hasUrlPattern && baseUrlInvalid ? urlHint : undefined}
                    required={!readOnly && !hasUrlPattern}
                />
            </div>
            {hasRequiredQueryParam && (
                <FormInnerSection
                    titleKey={`${TAB_PREFIX}.${requiredQueryParamKey.toUpperCase()}_LABEL`}
                    descriptionKey={`${TAB_PREFIX}.${requiredQueryParamKey.toUpperCase()}_DESCRIPTION`}
                >
                    <Input
                        type="textarea"
                        inputConfig={{ rows: 1 }}
                        value={requiredQueryParamValue}
                        placeholder={
                            readOnly
                                ? undefined
                                : translate(`${TAB_PREFIX}.${requiredQueryParamKey.toUpperCase()}_PLACEHOLDER`)
                        }
                        onChange={val => {
                            // Same pattern as the security-token input: clear when nothing else
                            // is set, otherwise weave onto the assembled URL.
                            if (!val && !domainValue && !securityTokenValue) {
                                setField('postback_url_base', undefined);
                                return;
                            }
                            const baseUrl =
                                tabData.postback_url_base ||
                                buildPostbackUrlForTemplate(platformTemplate, domainValue || '');
                            setField('postback_url_base', setRequiredQueryParamInUrl(platformTemplate, baseUrl, val));
                        }}
                        disabled={readOnly}
                        required={!readOnly}
                        containerClass={css.securityTokenInput}
                        inputClassName={css.securityTokenTextArea}
                        error={requiredQueryParamInvalid ? requiredQueryParamHint : undefined}
                    />
                </FormInnerSection>
            )}
            {hasSecurityToken && (
                <FormInnerSection
                    titleKey={`${TAB_PREFIX}.SECURITY_TOKEN_LABEL`}
                    descriptionKey={`${TAB_PREFIX}.SECURITY_TOKEN_DESCRIPTION`}
                >
                    <Input
                        type="textarea"
                        inputConfig={{ rows: 1 }}
                        value={securityTokenValue}
                        placeholder={readOnly ? undefined : translate(`${TAB_PREFIX}.SECURITY_TOKEN_PLACEHOLDER`)}
                        onChange={val => {
                            // All pieces empty → clear the field. Otherwise weave the token onto
                            // the assembled URL (using empty domain when the user hasn't typed
                            // one yet — token alone is allowed as a mid-edit state).
                            if (!val && !domainValue && !requiredQueryParamValue) {
                                setField('postback_url_base', undefined);
                                return;
                            }
                            const baseUrl =
                                tabData.postback_url_base ||
                                buildPostbackUrlForTemplate(platformTemplate, domainValue || '');
                            setField('postback_url_base', setSecurityTokenInUrl(platformTemplate, baseUrl, val));
                        }}
                        disabled={readOnly}
                        containerClass={css.securityTokenInput}
                        inputClassName={css.securityTokenTextArea}
                        error={securityTokenInvalid ? securityTokenHint : undefined}
                    />
                </FormInnerSection>
            )}
            {method === POSTBACK_METHODS.POST && (
                <WarningMessage
                    type="info"
                    className={css.postBodyInfo}
                    showIcon={false}
                    message={
                        <div className={css.postBodyContent}>
                            <div className={css.postBodyLabel}>
                                <Translate id={`${TAB_PREFIX}.POST_BODY_LABEL`} />
                                <QuestionMark message={`${TAB_PREFIX}.POST_BODY_TOOLTIP`} />
                            </div>
                            <div className={css.postBodyExplanation}>
                                <Translate id={`${TAB_PREFIX}.POST_BODY_EXPLANATION`} />
                            </div>
                            <code className={css.postBodyExample}>
                                <Translate id={`${TAB_PREFIX}.POST_BODY_EXAMPLE`} />
                            </code>
                        </div>
                    }
                />
            )}
            <FormInnerSection
                titleKey={`${TAB_PREFIX}.DIFFERENT_DOMAINS_LABEL`}
                descriptionKey={`${TAB_PREFIX}.DIFFERENT_DOMAINS_SUBTITLE`}
                disabledMessageKey={
                    !readOnly && !hasAnySelection ? `${TAB_PREFIX}.DIFFERENT_DOMAINS_DISABLED_MESSAGE` : undefined
                }
                headerRight={
                    <Toggle
                        checked={differentDomains}
                        onToggle={handleDifferentDomainsToggle}
                        disabled={readOnly || !hasAnySelection}
                    />
                }
                anchor="postback_urls"
            >
                {differentDomains &&
                    selectedEventTypes.map(eventType => {
                        const field = urlField(eventType);
                        const rawValue = tabData[field] || '';
                        // Per-event URL pattern is event-type specific (HasOffers conversions
                        // uses `/aff_lsr` instead of `/aff_goal`); pass eventType so extract
                        // and build use the right pattern.
                        const eventDomainValue = hasUrlPattern
                            ? extractPostbackUrlDomain(platformTemplate, rawValue, eventType)
                            : '';
                        // Build the preview from the (template, event_type) pattern + whichever
                        // domain applies: per-event domain if filled, otherwise the shared base
                        // domain (or the `your-domain` placeholder if neither is filled). Always
                        // assemble via the event-type pattern so per-event path overrides
                        // (e.g. HasOffers conversions = /aff_lsr) take effect even when the row
                        // is falling back to the shared domain.
                        const urlPreview = (() => {
                            if (!hasUrlPattern) {
                                return eventDomainValue ? rawValue : displayedBaseUrl;
                            }
                            const previewDomain = eventDomainValue || domainValue || domainPlaceholder;
                            let url = buildPostbackUrlForTemplate(platformTemplate, previewDomain, eventType);
                            if (requiredQueryParamValue) {
                                url = setRequiredQueryParamInUrl(platformTemplate, url, requiredQueryParamValue);
                            }
                            if (securityTokenValue) {
                                url = setSecurityTokenInUrl(platformTemplate, url, securityTokenValue);
                            }
                            return url;
                        })();
                        return (
                            <div key={eventType} className={css.eventUrlField}>
                                <Label
                                    text={`${TAB_PREFIX}.EVENT_TYPES.${eventType.toUpperCase()}`}
                                    type="p"
                                    className={css.eventUrlLabel}
                                />
                                {hasUrlPattern ? (
                                    <div className={css.domainRow}>
                                        <Input
                                            value={eventDomainValue}
                                            placeholder={
                                                readOnly
                                                    ? undefined
                                                    : translate(`${TAB_PREFIX}.POSTBACK_URL_DOMAIN_PLACEHOLDER`)
                                            }
                                            onChange={val =>
                                                setField(
                                                    field,
                                                    val
                                                        ? buildPostbackUrlForTemplate(platformTemplate, val, eventType)
                                                        : undefined
                                                )
                                            }
                                            disabled={readOnly}
                                            containerClass={css.eventDomainInput}
                                            error={
                                                eventDomainValue && !POSTBACK_URL_DOMAIN_REGEX.test(eventDomainValue)
                                                    ? domainHint
                                                    : undefined
                                            }
                                        />
                                        {urlPreview && <code className={css.urlPreview}>{urlPreview}</code>}
                                    </div>
                                ) : (
                                    <Input
                                        value={rawValue}
                                        placeholder={
                                            readOnly
                                                ? undefined
                                                : translate(
                                                      `${TAB_PREFIX}.POSTBACK_URL_PLACEHOLDERS.${eventType.toUpperCase()}`
                                                  )
                                        }
                                        onChange={val => setField(field, val)}
                                        disabled={readOnly}
                                        containerClass={css.eventUrlInput}
                                        error={eventUrlInvalid(eventType) ? urlHint : undefined}
                                    />
                                )}
                            </div>
                        );
                    })}
            </FormInnerSection>
            {dialog}
        </FormInnerSection>
    );
};

PostbackUrlBaseSection.propTypes = {
    translate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default withLocalize(PostbackUrlBaseSection);
